import { FocusTrap } from '@app/components/various/FocusTrap';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { cn } from '@app/utils/cn';
import { useIsScreenSize, useOnValueChange } from '@app/utils/hooks';
import { usePopover } from '@app/utils/hooks/usePopover';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useHover } from 'react-laag';
import { ArrowProps } from 'react-laag/dist/Arrow';
import { PlacementType } from 'react-laag/dist/PlacementType';

import { DropdownDrawer } from '../Dropdown/DropdownDrawer';
import { Portal } from '../Portal';
import { Scrim } from '../Scrim';

import { PopoverSheet } from './PopoverSheet';

const HOVER_DELAY = 100;
const DEFAULT_TRIGGER_OFFSET = 4;
const DEFAULT_POSSIBLE_PLACEMENTS: PlacementType[] = ['top-start', 'top-center', 'top-end', 'bottom-start', 'bottom-center', 'bottom-end'];

interface Props {
  arrowProps?: ArrowProps;
  children: React.ReactNode | ((props: { isVisible: boolean }) => React.ReactNode);
  className?: string;
  triggerClassName?: string;
  content: React.ReactNode | ((props: { handleClose: VoidFunction }) => React.ReactNode);
  contentHeader?: string;
  isContentHoverable?: boolean;
  overflowContainer?: boolean;
  placement?: PlacementType;
  possiblePlacements?: PlacementType[];
  shouldReturnFocusAfterClose?: boolean;
  snap?: boolean;
  triggerOffset?: number;
  containerOffset?: number;
  onDisappear?: () => void;
  onAppear?: () => void;
  onParentClose?: () => void;
  isClickable?: boolean;
  isEnabled?: boolean;
}

export const Popover = ({
  children,
  className,
  triggerClassName,
  content,
  contentHeader,
  isContentHoverable = true,
  overflowContainer,
  placement = DEFAULT_POSSIBLE_PLACEMENTS[0],
  possiblePlacements = DEFAULT_POSSIBLE_PLACEMENTS,
  shouldReturnFocusAfterClose,
  snap = false,
  triggerOffset = DEFAULT_TRIGGER_OFFSET,
  containerOffset,
  onParentClose,
  onDisappear,
  onAppear,
  isClickable = false,
  isEnabled = true,
}: Props) => {
  const [isOver, hoverProps] = useHover({
    delayEnter: HOVER_DELAY,
    delayLeave: HOVER_DELAY,
    hideOnScroll: false,
  });
  const [isOpen, setIsOpen] = React.useState(false);

  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const isVisible = isEnabled && (isClickable || isMobile ? isOpen : isOver);

  const { layerProps, triggerProps, layerSide, renderLayer } = usePopover({
    escapeHandlerEnabled: isVisible,
    handleDisappear: () => {
      onDisappear?.();
      isClickable && setIsOpen(false);
    },
    layerProps: {
      containerOffset,
      isOpen: isVisible && !isMobile,
      onParentClose,
      overflowContainer,
      placement,
      possiblePlacements,
      snap,
      triggerOffset,
    },
  });

  useOnValueChange(isVisible, () => {
    isVisible ? onAppear?.() : onDisappear?.();
  });

  const clickProps = {
    onClick: () => {
      setIsOpen(current => !current);
    },
  };

  const handleClose = () => {
    setIsOpen(false);
    onDisappear?.();
  };

  if (isMobile) {
    return (
      <>
        <div className="inline" {...clickProps}>
          {typeof children === 'function' ? children({ isVisible }) : children}
        </div>
        <Portal>
          <Scrim isVisible={isVisible} onClick={handleClose} />
          <DropdownDrawer title={contentHeader} isOpen={isVisible} className={className} onClose={handleClose}>
            {typeof content === 'function' ? content({ handleClose }) : content}
          </DropdownDrawer>
        </Portal>
      </>
    );
  }

  return (
    <>
      <div className={cn('inline', triggerClassName)} {...triggerProps} {...(isClickable ? clickProps : hoverProps)}>
        {typeof children === 'function' ? children({ isVisible }) : children}
      </div>
      {renderLayer(
        <PopoverSheet
          {...layerProps}
          {...(!isClickable && isContentHoverable && hoverProps)}
          className={className}
          isOpen={isVisible}
          layerSide={layerSide}
          title={contentHeader}
          onClose={isClickable && isDefined(contentHeader) ? handleClose : undefined}
        >
          <FocusTrap shouldReturnFocus={shouldReturnFocusAfterClose ?? isClickable}>
            {typeof content === 'function' ? content({ handleClose }) : content}
          </FocusTrap>
        </PopoverSheet>,
      )}
    </>
  );
};
